import { baseApi, IPaginatedResponse } from './baseApi';
import { IDataRecordStats, IFileNode, IFileTreeNode, IJobInputFilesQuery } from './filesApi.types';
import { utf8ToBase64 } from './utils';
import { downloadAsFile } from '../utils';

// Preserve trailing slash behavior
const getParentDirectoryPath = (directoryPath: string): string => {
    const hasTrailingSlash = directoryPath.endsWith('/');
    const normalizedPath = directoryPath.replace(/\/$/, '');
    const parentPath = normalizedPath.substring(0, normalizedPath.lastIndexOf('/'));
    if (parentPath === '') {
        return '/';
    }
    return hasTrailingSlash ? parentPath + '/' : parentPath;
};

export const filesApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        getJobInputFiles: build.query<
            IPaginatedResponse<IFileNode>,
            {
                jobUuid: string;
                jobAuthToken?: string;
                query: IJobInputFilesQuery;
            }
        >({
            query: (args) => ({
                url: `/proxy/files/jobs/${args.jobUuid}/input/query/`,
                method: 'POST',
                headers: args.jobAuthToken ? { 'Job-Auth-Token': args.jobAuthToken } : undefined,
                body: args.query,
            }),
            transformResponse: (response: IPaginatedResponse<IFileNode>, meta, args) => {
                const dir_path = args.query.exact_directory_path_filter;
                const parentDirectoryPath = getParentDirectoryPath(dir_path);
                return {
                    ...response,
                    results:
                        dir_path === '/'
                            ? response.results
                            : [
                                  {
                                      name: '..',
                                      size: 0,
                                      is_dir: true,
                                      dir_path: parentDirectoryPath,
                                  },
                                  ...response.results,
                              ],
                };
            },
        }),
        getFileTreeNode: build.query<IFileTreeNode, { presignedStorageUrl: string; path: string }>({
            query: (args) => ({
                url: `${location.origin}/proxy/files/v1/get/`,
                params: {
                    upstream_url_base64: utf8ToBase64(args.presignedStorageUrl),
                    path_base64: utf8ToBase64(args.path),
                },
            }),
        }),
        listFileTreeNodes: build.query<
            IPaginatedResponse<IFileTreeNode> & { data_record_stats?: IDataRecordStats },
            {
                presignedStorageUrl: string;
                directoryPath: string;
            }
        >({
            query: (args) => ({
                url: `${location.origin}/proxy/files/v1/tree/`,
                params: {
                    upstream_url_base64: utf8ToBase64(args.presignedStorageUrl),
                    path_base64: utf8ToBase64(args.directoryPath),
                },
            }),
            transformResponse: (
                response: IPaginatedResponse<IFileTreeNode> & { data_record_stats?: IDataRecordStats },
                meta,
                args,
            ) => {
                const parentDirectoryPath = getParentDirectoryPath(args.directoryPath);
                const isAtNavigationRoot = args.directoryPath === '/';
                return {
                    ...response,
                    results: isAtNavigationRoot
                        ? response.results
                        : [
                              {
                                  name: '..',
                                  size_in_bytes: 0,
                                  is_directory: true,
                                  full_path: parentDirectoryPath,
                              },
                              ...response.results,
                          ],
                };
            },
        }),
        downloadInputFile: build.mutation<void, { resultUuid: string; filePath: string; resultAuthToken?: string }>({
            query: (args) => ({
                url: `/jobs/${args.resultUuid}/storage/results/download/`,
                headers: args.resultAuthToken ? { 'Job-Auth-Token': args.resultAuthToken } : {},
            }),
            transformResponse: (
                response: { presigned_download_url: string; presigned_input_download_url: string },
                meta,
                args,
            ) => {
                const { presigned_input_download_url } = response;
                const fileName = args.filePath.split('/').pop();
                const fileUrl = `${location.origin}/api/proxy/files/jobs/${args.resultUuid}/input/data/?upstream_url_base64=${utf8ToBase64(presigned_input_download_url)}&path_base64=${utf8ToBase64(args.filePath)}`;
                downloadAsFile(fileUrl, fileName);
            },
        }),
    }),
});

export const {
    useGetFileTreeNodeQuery,
    useGetJobInputFilesQuery,
    useListFileTreeNodesQuery,
    useDownloadInputFileMutation,
} = filesApi;
